// src/pages/Donazioni.js – Pagina obiettivi/donation goals (versione compatta)

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Heading,
  InputGroup,
  InputRightElement,
  Text,
  SimpleGrid,
  Progress,
  HStack,
  NumberInput,
  NumberInputField,
  Image,
  Icon,
  useColorModeValue,
  Skeleton,
  SkeletonText,
  VStack,
  Container,
} from '@chakra-ui/react';
import { FaGamepad, FaHeart } from 'react-icons/fa';
import DonationDrawer from '../components/Donations/DonationDrawer';

const cilColors = {
  yellow: '#ffcc00',
  yellowDark: '#e6b800',
  dark: '#1a1a1a',
  plum: '#3b235a',
};

const API_URL = process.env.REACT_APP_STRAPI_URL || '';
const withBase = (url) =>
  url?.startsWith('http') ? url : API_URL ? `${API_URL}${url || ''}` : url;
const track = (event, payload = {}) => {
  try {
    console.debug('[track]', event, payload);
  } catch (e) {
    // ignore tracking errors
  }
};

const HERO_IMAGE =
  'https://cms.cilassociazione.it/uploads/carnevale_munlab_d7caee7803.jpg';

const Donazioni = () => {
  const [goals, setGoals] = useState([]);
  const [loadingGoals, setLoadingGoals] = useState(true);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [donationAmount, setDonationAmount] = useState(0);
  const [customAmounts, setCustomAmounts] = useState({});
  const [heroAmount, setHeroAmount] = useState(20);
  const [openFreeDonation, setOpenFreeDonation] = useState(false);
  const cardBg = useColorModeValue('white', 'gray.800');

  useEffect(() => {
    const fetchGoals = async () => {
      try {
        const response = await fetch('/api/donation-goals');
        if (response.ok) {
          const data = await response.json();
          const goalsArray = Array.isArray(data)
            ? data
            : data?.data && Array.isArray(data.data)
              ? data.data
              : [];
          setGoals(goalsArray);
        }
      } catch (error) {
        console.error('Errore nel caricamento dei goal:', error);
        setGoals([]);
      } finally {
        setLoadingGoals(false);
      }
    };
    fetchGoals();
  }, []);

  const openDonation = (goal, amount) => {
    setSelectedGoal(goal);
    setDonationAmount(amount);
  };

  const memoizedGoals = useMemo(() => {
    if (!Array.isArray(goals) || goals.length === 0) return [];
    return [...goals].sort((a, b) => {
      const aTarget = Number(a.targetAmount) || 0;
      const aDonated =
        Number(a.currentAmount) ||
        (a.donations || [])
          .filter((d) => d?.donationStatus === 'succeeded')
          .reduce((sum, d) => sum + (Number(d?.amount) || 0), 0);
      const aIsCompleted = aDonated >= aTarget && aTarget > 0;
      const bTarget = Number(b.targetAmount) || 0;
      const bDonated =
        Number(b.currentAmount) ||
        (b.donations || [])
          .filter((d) => d?.donationStatus === 'succeeded')
          .reduce((sum, d) => sum + (Number(d?.amount) || 0), 0);
      const bIsCompleted = bDonated >= bTarget && bTarget > 0;
      return aIsCompleted - bIsCompleted;
    });
  }, [goals]);

  const handleHeroDonate = () => {
    const amount = Number(heroAmount) || 0;
    if (amount > 0) {
      setDonationAmount(amount);
      setSelectedGoal(null);
      setOpenFreeDonation(true);
      track('cta_donazione_hero', { amount });
    }
  };

  return (
    <Box bg="gray.50" minH="100vh" color="white" pb={8}>
      {/* HERO - titolo, descrizione; su desktop a destra input importo + pulsante Dona */}
      <Box
        position="relative"
        minHeight={{ base: '320px', md: '360px' }}
        display="flex"
        alignItems="center"
        overflow="hidden"
      >
        <Box
          position="absolute"
          inset={0}
          pointerEvents="none"
          backgroundImage={`url(${withBase(HERO_IMAGE) || '/fallback-hero.jpg'})`}
          backgroundPosition="center"
          backgroundSize="cover"
        />
        <Box
          position="absolute"
          inset={0}
          pointerEvents="none"
          bgGradient="linear(to-b, rgba(59, 35, 90, 0.85) 0%, rgba(59, 35, 90, 0.7) 50%, rgba(26, 26, 26, 0.9) 100%)"
        />
        <Container
          maxW="container.xl"
          position="relative"
          zIndex={1}
          py={{ base: 10, md: 12 }}
          px={5}
        >
          <VStack
            spacing={8}
            w="full"
            align="center"
            justify="center"
            textAlign="center"
          >
            <Heading
              as="h1"
              fontSize={{ base: '2xl', md: '3xl' }}
              fontWeight="800"
              letterSpacing="-0.02em"
              textShadow="0 2px 12px rgba(0,0,0,0.4)"
            >
              Dona a CIL
            </Heading>
            <Text
              fontSize={{ base: 'md', md: 'lg' }}
              color="whiteAlpha.95"
              lineHeight="1.6"
              textShadow="0 1px 4px rgba(0,0,0,0.3)"
              maxW="560px"
            >
              Scegli un importo e dona subito. Ogni euro sostiene i nostri
              progetti e le attività per la comunità.
            </Text>
            <HStack spacing={3} align="stretch" justify="center">
              <InputGroup w={{ base: 'full', md: '160px' }}>
                <NumberInput
                  min={1}
                  value={heroAmount}
                  onChange={(val) => setHeroAmount(Number(val) || 0)}
                  w="full"
                >
                  <NumberInputField
                    pr={9}
                    textAlign="right"
                    bg="white"
                    color="gray.800"
                    borderColor="gray.200"
                    borderRadius="md"
                    fontWeight="semibold"
                    fontSize="md"
                    _placeholder={{ color: 'gray.500' }}
                    _focus={{
                      borderColor: cilColors.yellow,
                      boxShadow: `0 0 0 2px ${cilColors.yellow}`,
                    }}
                  />
                </NumberInput>
                <InputRightElement
                  pointerEvents="none"
                  color="gray.500"
                  fontSize="md"
                  pr={3}
                >
                  €
                </InputRightElement>
              </InputGroup>
              <Button
                leftIcon={<FaHeart />}
                bg={cilColors.yellow}
                color={cilColors.plum}
                fontWeight="bold"
                _hover={{ bg: cilColors.yellowDark }}
                onClick={handleHeroDonate}
                isDisabled={!heroAmount || heroAmount <= 0}
                px={6}
              >
                Dona
              </Button>
            </HStack>
          </VStack>
        </Container>
      </Box>

      {/* DONATION GOALS - compatto */}
      <Box id="donation-goals" py={6} position="relative">
        <Box
          position="absolute"
          inset={0}
          opacity="0.04"
          bgImage="radial-gradient(circle, #ffcc00 1px, transparent 1px)"
          bgSize="24px 24px"
          pointerEvents="none"
        />
        <Container maxW="1100px" position="relative" zIndex={1}>
          <VStack spacing={6}>
            <HStack spacing={2} justify="center">
              <Icon as={FaGamepad} w={6} h={6} color={cilColors.dark} />
              <Heading size="xl" color={cilColors.dark} fontWeight="extrabold">
                Sostieni i Nostri Progetti
              </Heading>
            </HStack>
            <Text
              textAlign="center"
              fontSize="sm"
              color="gray.600"
              maxW="560px"
            >
              Ogni obiettivo è una raccolta per un progetto specifico. Scegli
              quello che ti appassiona e dona.
            </Text>

            {loadingGoals ? (
              <SimpleGrid columns={[1, null, 3]} spacing={4} w="full">
                {[1, 2, 3].map((i) => (
                  <Box
                    key={`skeleton-${i}`}
                    bg="white"
                    borderRadius="md"
                    overflow="hidden"
                    boxShadow="sm"
                  >
                    <Skeleton height="120px" />
                    <Box p={4}>
                      <Skeleton height="20px" mb={2} />
                      <SkeletonText noOfLines={2} />
                      <Skeleton height="32px" mt={3} />
                    </Box>
                  </Box>
                ))}
              </SimpleGrid>
            ) : !Array.isArray(memoizedGoals) || memoizedGoals.length === 0 ? (
              <Text py={6} color="gray.500">
                Nuovi obiettivi in arrivo
              </Text>
            ) : (
              <SimpleGrid columns={[1, null, 3]} spacing={5} w="full">
                {memoizedGoals.map((goal) => {
                  const {
                    documentId,
                    title,
                    description,
                    targetAmount,
                    currentAmount,
                    donations = [],
                    image,
                  } = goal;
                  const target = Number(targetAmount) || 0;
                  const donated =
                    Number(currentAmount) ||
                    (donations || [])
                      .filter((d) => d?.donationStatus === 'succeeded')
                      .reduce((sum, d) => sum + (Number(d?.amount) || 0), 0);
                  const progress =
                    target > 0 ? Math.min(100, (donated / target) * 100) : 0;
                  const donationCount = donations.filter(
                    (d) => d.donationStatus === 'succeeded'
                  ).length;
                  const isCompleted = donated >= target && target > 0;
                  const imgSrcRaw =
                    image?.[0]?.formats?.medium?.url || image?.[0]?.url;
                  const imgSrc = imgSrcRaw
                    ? withBase(imgSrcRaw)
                    : '/default-goal-image.png';

                  return (
                    <Box
                      key={documentId}
                      position="relative"
                      bg={cardBg}
                      borderRadius="md"
                      overflow="hidden"
                      boxShadow="md"
                      textAlign="center"
                      cursor={isCompleted ? 'default' : 'pointer'}
                      borderWidth="2px"
                      borderColor={isCompleted ? 'green.400' : 'transparent'}
                      _hover={
                        isCompleted
                          ? {}
                          : { shadow: 'lg', borderColor: cilColors.yellow }
                      }
                    >
                      {isCompleted && (
                        <Box
                          position="absolute"
                          top={0}
                          left="50%"
                          transform="translateX(-50%)"
                          bg="green.400"
                          color="white"
                          px={3}
                          py={0.5}
                          fontSize="xs"
                          fontWeight="bold"
                          borderRadius="0 0 4px 4px"
                          zIndex={2}
                        >
                          COMPLETATO
                        </Box>
                      )}
                      <Image
                        src={imgSrc}
                        alt={title}
                        w="100%"
                        h="140px"
                        objectFit="cover"
                        filter={isCompleted ? 'grayscale(30%)' : 'none'}
                      />
                      <Box p={4} position="relative">
                        <Heading
                          size="sm"
                          mb={2}
                          color={isCompleted ? 'green.600' : cilColors.plum}
                        >
                          {title}
                        </Heading>
                        <Text
                          fontSize="xs"
                          mb={3}
                          color="gray.600"
                          noOfLines={2}
                        >
                          {description}
                        </Text>
                        <Progress
                          value={progress}
                          size="sm"
                          borderRadius="full"
                          colorScheme={isCompleted ? 'green' : 'yellow'}
                          mb={2}
                        />
                        <Text
                          fontSize="sm"
                          fontWeight="bold"
                          color={isCompleted ? 'green.600' : 'gray.700'}
                        >
                          €{donated?.toFixed?.(2) ?? '0.00'} / €
                          {target?.toFixed?.(2) ?? '0.00'}
                        </Text>
                        <Text fontSize="xs" color="gray.500" mb={3}>
                          {donationCount} donazioni ·{' '}
                          {progress?.toFixed?.(1) ?? 0}%
                        </Text>

                        {isCompleted ? (
                          <Text
                            fontSize="xs"
                            color="green.700"
                            fontWeight="semibold"
                          >
                            🎉 Obiettivo raggiunto!
                          </Text>
                        ) : (
                          <>
                            <HStack spacing={2} justify="center" mb={3}>
                              {[5, 10, 20].map((amt) => (
                                <Button
                                  key={amt}
                                  size="sm"
                                  onClick={() => openDonation(goal, amt)}
                                  bg={cilColors.yellow}
                                  color="white"
                                  _hover={{ bg: cilColors.yellowDark }}
                                  fontWeight="bold"
                                >
                                  €{amt}
                                </Button>
                              ))}
                            </HStack>
                            <HStack justify="center" spacing={2}>
                              <InputGroup w="100px">
                                <NumberInput
                                  min={1}
                                  size="sm"
                                  w="full"
                                  value={customAmounts[documentId] || ''}
                                  onChange={(val) =>
                                    setCustomAmounts((prev) => ({
                                      ...prev,
                                      [documentId]: val,
                                    }))
                                  }
                                >
                                  <NumberInputField
                                    placeholder="Altro"
                                    pr={7}
                                    textAlign="right"
                                    bg="white"
                                    color="gray.800"
                                    borderColor="gray.200"
                                    _focus={{ borderColor: cilColors.yellow }}
                                  />
                                </NumberInput>
                                <InputRightElement
                                  pointerEvents="none"
                                  color="gray.500"
                                  fontSize="sm"
                                  pr={2}
                                >
                                  €
                                </InputRightElement>
                              </InputGroup>
                              {customAmounts[documentId] &&
                                Number(customAmounts[documentId]) > 0 && (
                                  <Button
                                    size="sm"
                                    leftIcon={<FaHeart />}
                                    bg={cilColors.yellow}
                                    color="white"
                                    _hover={{ bg: cilColors.yellowDark }}
                                    onClick={() => {
                                      const v = Number(
                                        customAmounts[documentId]
                                      );
                                      if (v > 0) {
                                        openDonation(goal, v);
                                        track(`cta_dona_goal_${documentId}`, {
                                          amount: v,
                                        });
                                      }
                                    }}
                                  >
                                    Dona €{customAmounts[documentId]}
                                  </Button>
                                )}
                            </HStack>
                          </>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </SimpleGrid>
            )}
          </VStack>
        </Container>
      </Box>

      {selectedGoal && (
        <DonationDrawer
          isOpen
          onClose={() => {
            setSelectedGoal(null);
            setDonationAmount(0);
          }}
          goal={selectedGoal}
          amount={donationAmount}
        />
      )}
      {openFreeDonation && (
        <DonationDrawer
          isOpen
          onClose={() => {
            setOpenFreeDonation(false);
            setHeroAmount(20);
          }}
          goal={null}
          amount={donationAmount > 0 ? donationAmount : heroAmount || 20}
        />
      )}
    </Box>
  );
};

export default Donazioni;
